import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONTACT_INFO = {
  phone: "9899928308",
  emails: ["contact@niryasaindustries.com", "niryasaindustries@gmail.com"],
  address: "Village Ramnagar, Ganaur, Haryana 131039, India",
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", product: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", product: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Failed to send. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0B0E12] py-24 md:py-32 border-t border-[#F5F1EA]/8"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-brand-red" />
            <span data-testid="contact-eyebrow" className="text-xs uppercase tracking-[0.3em] text-brand-red font-medium">
              Get in Touch
            </span>
          </div>
          <h2
            data-testid="contact-heading"
            className="font-display font-semibold tracking-tight text-white text-3xl md:text-4xl lg:text-5xl leading-[1.05]"
          >
            Let&apos;s Build <br />
            <span className="text-brand-red italic font-medium">Something Solid.</span>
          </h2>
          <p data-testid="contact-description" className="mt-6 text-base md:text-lg text-white/60 max-w-xl leading-relaxed">
            Bulk orders, custom specs, or just a quote — drop us a message and our team will respond within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: Contact details */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <a
              href={`tel:${CONTACT_INFO.phone}`}
              data-testid="contact-phone-card"
              className="group flex items-start gap-5 bg-[#161A21] border border-[#F5F1EA]/10 p-6 hover:border-brand-red/50 transition-colors"
            >
              <div className="p-3 bg-brand-red/10 border border-brand-red/30 flex-shrink-0">
                <Phone size={20} className="text-brand-red" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">Phone</div>
                <div className="font-display text-xl font-bold text-white group-hover:text-brand-red transition-colors">
                  +91 {CONTACT_INFO.phone}
                </div>
              </div>
            </a>

            <div data-testid="contact-email-card" className="flex items-start gap-5 bg-[#161A21] border border-[#F5F1EA]/10 p-6">
              <div className="p-3 bg-brand-red/10 border border-brand-red/30 flex-shrink-0">
                <Mail size={20} className="text-brand-red" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-2">Email</div>
                <div className="flex flex-col gap-1">
                  {CONTACT_INFO.emails.map((email, i) => (
                    <a
                      key={i}
                      href={`mailto:${email}`}
                      data-testid={`contact-email-link-${i}`}
                      className="text-sm md:text-base text-white hover:text-brand-red transition-colors break-all"
                    >
                      {email}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div data-testid="contact-address-card" className="flex items-start gap-5 bg-[#161A21] border border-[#F5F1EA]/10 p-6">
              <div className="p-3 bg-brand-red/10 border border-brand-red/30 flex-shrink-0">
                <MapPin size={20} className="text-brand-red" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">Factory Address</div>
                <div className="text-base text-white leading-relaxed">
                  {CONTACT_INFO.address}
                </div>
              </div>
            </div>

            {/* Hours / Trust */}
            <div className="bg-brand-red p-6 mt-2">
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 mb-2">Business Hours</div>
              <div className="font-display text-lg font-bold text-white uppercase">Mon — Sat &nbsp; 9:00 AM — 7:00 PM IST</div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={handleSubmit}
              className="bg-[#161A21] border border-[#F5F1EA]/10 p-8 md:p-10 flex flex-col gap-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField label="Full Name" name="name" value={form.name} onChange={handleChange} testId="form-name" required />
                <FormField label="Email" name="email" type="email" value={form.email} onChange={handleChange} testId="form-email" required />
                <FormField label="Phone" name="phone" type="tel" value={form.phone} onChange={handleChange} testId="form-phone" required />
                <FormField label="Interested In" name="product" value={form.product} onChange={handleChange} testId="form-product" placeholder="Cables / Hoses / Both" />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-medium">
                  Message <span className="text-brand-red">*</span>
                </label>
                <textarea
                  data-testid="form-message"
                  name="message"
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  required
                  placeholder="Tell us about your requirement..."
      className="bg-[#0B0E12] border border-[#F5F1EA]/15 text-white px-4 py-3 focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red transition-colors placeholder:text-white/30 resize-none"
                />
              </div>

              <button
                data-testid="form-submit"
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-3 bg-brand-red text-white px-8 py-5 uppercase tracking-wider text-sm font-bold hover:bg-brand-redHover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send Inquiry
                    <Send size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const FormField = ({ label, name, value, onChange, type = "text", testId, required, placeholder }) => (
  <div className="flex flex-col gap-2">
    <label className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-medium">
      {label} {required && <span className="text-brand-red">*</span>}
    </label>
    <input
      data-testid={testId}
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      placeholder={placeholder}
      className="bg-[#0B0E12] border border-[#F5F1EA]/15 text-white px-4 py-3 focus:border-brand-red focus:outline-none focus:ring-1 focus:ring-brand-red transition-colors placeholder:text-white/30"
    />
  </div>
);
